import { doc, getDoc, setDoc, updateDoc, query, where, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Language codes supported by Google Translate
export const SUPPORTED_LANGUAGES = {
  'auto': 'Auto-detect',
  'af': 'Afrikaans',
  'en': 'English',
  'es': 'Spanish',
  'fr': 'French',
  'de': 'German',
  'it': 'Italian',
  'pt': 'Portuguese',
  'ru': 'Russian',
  'ja': 'Japanese',
  'ko': 'Korean',
  'zh': 'Chinese (Simplified)',
  'zh-TW': 'Chinese (Traditional)',
  'ar': 'Arabic',
  'hi': 'Hindi',
  'th': 'Thai',
  'vi': 'Vietnamese',
  'tr': 'Turkish',
  'pl': 'Polish',
  'nl': 'Dutch',
  'sv': 'Swedish',
  'da': 'Danish',
  'no': 'Norwegian',
  'fi': 'Finnish',
  'he': 'Hebrew',
  'bg': 'Bulgarian',
  'cs': 'Czech',
  'et': 'Estonian',
  'lv': 'Latvian',
  'lt': 'Lithuanian',
  'sk': 'Slovak',
  'sl': 'Slovenian',
  'hr': 'Croatian',
  'hu': 'Hungarian',
  'ro': 'Romanian',
  'uk': 'Ukrainian',
};

export interface TranslationCache {
  id: string;
  sourceText: string;
  targetLanguage: string;
  translatedText: string;
  sourceLanguage?: string;
  confidence?: number;
  createdAt: any;
  lastUsed: any;
}

class TranslationService {
  private cache = new Map<string, string>();
  private detectedLanguageCache = new Map<string, string>();

  // Generate cache key for translation
  private getCacheKey(text: string, targetLang: string): string {
    // Create a simple hash of the text + target language that handles Unicode
    try {
      // Use a simple hash function that works with Unicode
      const textToHash = text.substring(0, 100);
      let hash = 0;
      for (let i = 0; i < textToHash.length; i++) {
        const char = textToHash.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      // Convert to positive number and add target language
      const hashString = Math.abs(hash).toString(36);
      return `${hashString}_${targetLang}`;
    } catch (error) {
      // Fallback: use a simple encoded version
      const encoded = encodeURIComponent(text.substring(0, 50)).replace(/[^a-zA-Z0-9]/g, '');
      return `${encoded}_${targetLang}`;
    }
  }

  // Detect user's browser language
  public getUserLanguage(): string {
    if (typeof window !== 'undefined') {
      const browserLang = navigator.language || navigator.languages[0];
      const langCode = browserLang.split('-')[0];
      return Object.keys(SUPPORTED_LANGUAGES).includes(langCode) ? langCode : 'en';
    }
    return 'en';
  }

  // Check if translation is needed (more permissive approach)
  public needsTranslation(text: string, targetLang: string): boolean {
    if (!text || text.trim().length === 0) return false;
    if (targetLang === 'auto') return false;
    
    // Very basic checks - be permissive to allow manual translation
    // Only skip very obvious cases
    const trimmedText = text.trim();
    
    // Skip very short text (likely not worth translating)
    if (trimmedText.length < 3) return false;
    
    // Skip if text is only numbers, punctuation, or symbols
    const onlySymbolsRegex = /^[0-9\s.,!?'"()\-:;@#$%^&*+=<>{}[\]|\\\/~`_]+$/;
    if (onlySymbolsRegex.test(trimmedText)) return false;
    
    // For everything else, allow translation attempt
    // This will show the translate button even for potential false positives
    // Better to offer translation when not needed than to miss actual foreign languages
    return true;
  }

  // Get translation from cache (Firestore)
  private async getFromCache(text: string, targetLang: string): Promise<string | null> {
    try {
      const cacheKey = this.getCacheKey(text, targetLang);
      
      // Check memory cache first
      if (this.cache.has(cacheKey)) {
        return this.cache.get(cacheKey)!;
      }

      // Check Firestore cache
      const cacheDoc = await getDoc(doc(db, 'translationCache', cacheKey));
      if (cacheDoc.exists()) {
        const data = cacheDoc.data() as TranslationCache;
        
        // The cache key already ensures uniqueness for text + target language
        // No need for exact string comparison which can cause cache misses
        // due to text normalization differences
        
        // Update last used timestamp
        await updateDoc(doc(db, 'translationCache', cacheKey), {
          lastUsed: new Date()
        });
        
        // Store in memory cache
        this.cache.set(cacheKey, data.translatedText);
        return data.translatedText;
      }
    } catch (error) {
      console.error('Error getting translation from cache:', error);
    }
    return null;
  }

  // Save translation to cache
  private async saveToCache(text: string, targetLang: string, translatedText: string, sourceLanguage?: string): Promise<void> {
    try {
      const cacheKey = this.getCacheKey(text, targetLang);
      const now = new Date();
      
      const cacheData: TranslationCache = {
        id: cacheKey,
        sourceText: text,
        targetLanguage: targetLang,
        translatedText,
        sourceLanguage,
        createdAt: now,
        lastUsed: now
      };

      await setDoc(doc(db, 'translationCache', cacheKey), cacheData);
      
      // Store in memory cache
      this.cache.set(cacheKey, translatedText);
    } catch (error) {
      console.error('Error saving translation to cache:', error);
    }
  }

  // Translate text using Google Translate API (via Cloud Function)
  public async translateText(text: string, targetLanguage: string, sourceLanguage: string = 'auto'): Promise<{
    translatedText: string;
    detectedSourceLanguage?: string;
  }> {
    try {
      // Check cache first
      const cachedTranslation = await this.getFromCache(text, targetLanguage);
      if (cachedTranslation) {
        return { translatedText: cachedTranslation };
      }

      // If source language is known and matches target, return original text
      if (sourceLanguage !== 'auto' && sourceLanguage === targetLanguage) {
        return { 
          translatedText: text, 
          detectedSourceLanguage: sourceLanguage 
        };
      }

      // Detect language first to avoid unnecessary translations
      let detectedLang = sourceLanguage;
      if (sourceLanguage === 'auto') {
        detectedLang = await this.detectLanguage(text);
        
        // If detected language matches target, return original text
        if (detectedLang === targetLanguage) {
          return { 
            translatedText: text, 
            detectedSourceLanguage: detectedLang 
          };
        }
      }

      // Call Cloud Function for translation
      const response = await fetch('https://us-central1-t1-fx-team.cloudfunctions.net/translateText', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          targetLanguage,
          sourceLanguage: detectedLang
        })
      });

      if (!response.ok) {
        throw new Error(`Translation failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Save to cache
      await this.saveToCache(text, targetLanguage, result.translatedText, result.detectedSourceLanguage);
      
      return result;
    } catch (error) {
      console.error('Translation error:', error);
      throw error;
    }
  }

  // Batch translate multiple texts
  public async translateBatch(texts: string[], targetLanguage: string): Promise<string[]> {
    try {
      const promises = texts.map(text => this.translateText(text, targetLanguage));
      const results = await Promise.all(promises);
      return results.map(result => result.translatedText);
    } catch (error) {
      console.error('Batch translation error:', error);
      throw error;
    }
  }

  // Clean up old cache entries (call periodically)
  public async cleanupCache(olderThanDays: number = 30): Promise<void> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);

      const oldCacheQuery = query(
        collection(db, 'translationCache'),
        where('lastUsed', '<', cutoffDate)
      );

      const snapshot = await getDocs(oldCacheQuery);
      const deletePromises = snapshot.docs.map(docSnap => deleteDoc(docSnap.ref));
      
      await Promise.all(deletePromises);
      console.log(`Cleaned up ${snapshot.docs.length} old translation cache entries`);
    } catch (error) {
      console.error('Error cleaning up translation cache:', error);
    }
  }

  // Detect language of text
  public async detectLanguage(text: string): Promise<string> {
    try {
      // Check cache first
      if (this.detectedLanguageCache.has(text)) {
        return this.detectedLanguageCache.get(text)!;
      }

      const response = await fetch('https://us-central1-t1-fx-team.cloudfunctions.net/detectLanguage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text })
      });

      if (!response.ok) {
        throw new Error(`Language detection failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Cache the result
      this.detectedLanguageCache.set(text, result.language);
      
      return result.language;
    } catch (error) {
      console.error('Language detection error:', error);
      return 'auto';
    }
  }
}

export const translationService = new TranslationService(); 